import React, { useState, useEffect } from "react";
import { ChevronRight, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ProfilePromptsStep = ({ onNext }) => {
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await api.getAvailablePrompts();
      setAvailablePrompts(response.data.prompts);
    } catch (error) {
      toast.error("Failed to load prompts");
    } finally {
      setLoading(false);
    }
  };

  const selectPrompt = (prompt) => {
    if (selectedPrompts.length >= 6) {
      toast.error("Maximum 6 prompts allowed");
      return;
    }
    if (selectedPrompts.find(p => p.id === prompt.id)) {
      return;
    }
    setSelectedPrompts(prev => [...prev, prompt]);
  };

  const removePrompt = (promptId) => {
    setSelectedPrompts(prev => prev.filter(p => p.id !== promptId));
    setAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[promptId];
      return newAnswers;
    });
  };

  const updateAnswer = (promptId, answer) => {
    setAnswers(prev => ({ ...prev, [promptId]: answer }));
  };

  const handleSubmit = async () => {
    if (selectedPrompts.length < 4) {
      toast.error("Please answer at least 4 prompts");
      return;
    }

    // Check all selected prompts have answers
    for (const prompt of selectedPrompts) {
      const answer = answers[prompt.id];
      if (!answer || answer.trim().length < 10) {
        toast.error(`Please provide a longer answer for: "${prompt.text.substring(0, 30)}..."`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const prompts = selectedPrompts.map(p => ({
        prompt_id: p.id,
        answer: answers[p.id].trim()
      }));

      const response = await api.submitProfilePrompts({ prompts });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1C3A7A]/30 border-t-[#1C3A7A] rounded-full animate-spin" />
      </div>
    );
  }

  const unselectedPrompts = availablePrompts.filter(
    p => !selectedPrompts.find(sp => sp.id === p.id)
  );

  return (
    <div className="bg-white rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Tell Your Story</h2>
      <p className="text-gray-600 mb-4">
        Choose at least 4 prompts to help others get to know you
      </p>

      {/* Selected Prompts with Answers */}
      {selectedPrompts.length > 0 && (
        <div className="space-y-4 mb-6">
          {selectedPrompts.map((prompt, index) => (
            <div key={prompt.id} className="border-2 border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <p className="text-sm font-medium text-[#1C3A7A] flex-1">{prompt.text}</p>
                <button
                  onClick={() => removePrompt(prompt.id)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <textarea
                value={answers[prompt.id] || ""}
                onChange={(e) => updateAnswer(prompt.id, e.target.value)}
                placeholder="Your answer..."
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent resize-none"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{answers[prompt.id]?.length || 0}/500</span>
                <span>{index + 1} of {selectedPrompts.length}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Available Prompts */}
      {selectedPrompts.length < 6 && (
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {selectedPrompts.length === 0 ? "Select prompts to answer:" : "Add more prompts:"}
          </p>
          <div className="space-y-2">
            {unselectedPrompts.map(prompt => (
              <button
                key={prompt.id}
                onClick={() => selectPrompt(prompt)}
                className="w-full px-4 py-3 rounded-xl border-2 border-dashed border-gray-300 text-left text-gray-600 hover:border-[#1C3A7A] hover:text-[#1C3A7A] transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{prompt.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-xl">
        <span className="text-sm text-gray-600">Prompts completed</span>
        <span className={`font-bold ${selectedPrompts.length >= 4 ? 'text-green-600' : 'text-[#1C3A7A]'}`}>
          {selectedPrompts.length}/4 minimum
        </span>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || selectedPrompts.length < 4}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default ProfilePromptsStep;
